import React, { Fragment, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useRevealOnScroll } from '../hooks/useRevealOnScroll';
import {
    Upload,
    MessageSquare,
    Github,
    Mail,
    ChevronDown,
    Menu,
    X,
    FileOutput,
    ArrowRight,
    Globe,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ShieldIcon from './ShieldIcon';
import { normalizeAppLocale } from '../i18n';
import { LandingLang, LANDING_LANG_OPTIONS, getLandingCopy } from '../lib/landingI18n';

const primaryBtn =
    'inline-flex items-center justify-center rounded-xl bg-brand-500 px-6 py-3.5 text-base font-semibold text-[#fcf8f8] shadow-md shadow-brand-500/20 transition-colors duration-200 ease-out hover:bg-brand-600 hover:shadow-lg hover:shadow-brand-500/28 active:brightness-[0.98]';

const productStepIcons = [Upload, MessageSquare, FileOutput] as const;

const faqLinkClass =
    'font-medium text-brand-500 underline decoration-brand-500/40 underline-offset-2 transition-colors hover:text-brand-600 hover:decoration-brand-600';

/** Inline accent link on solid backgrounds (product section demo jump link): keyboard focus matches landing chrome */
const landingAccentLinkFocus =
    'rounded-md outline-none focus-visible:ring-2 focus-visible:ring-brand-500/80 focus-visible:ring-offset-2 focus-visible:ring-offset-white';

const landingSectionInset =
    'pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] sm:pl-[max(1.5rem,env(safe-area-inset-left))] sm:pr-[max(1.5rem,env(safe-area-inset-right))] lg:pl-[max(2rem,env(safe-area-inset-left))] lg:pr-[max(2rem,env(safe-area-inset-right))]';

const revealChildBase =
    'transition-[opacity,transform] duration-[420ms] ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:transition-opacity motion-reduce:duration-200';

type RevealChildProps = React.PropsWithChildren<{
    revealed: boolean;
    delayMs?: number;
    className?: string;
}> &
    Omit<React.HTMLAttributes<HTMLDivElement>, 'className' | 'children'>;

const LandingLangSwitcher: React.FC<{
    lang: LandingLang;
    onChange: (next: LandingLang) => void;
    ariaLabel: string;
}> = ({ lang, onChange, ariaLabel }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    const current = LANDING_LANG_OPTIONS.find((o) => o.id === lang) ?? LANDING_LANG_OPTIONS[0];

    const close = useCallback(() => setOpen(false), []);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, close]);

    return (
        <div className="relative" ref={wrapRef}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-secondary-200 bg-white px-2.5 text-sm font-medium text-secondary-800 shadow-sm transition-colors hover:border-secondary-300 hover:bg-secondary-50 sm:px-3"
                aria-expanded={open}
                aria-haspopup="listbox"
                aria-label={ariaLabel}
            >
                <Globe className="h-4 w-4 shrink-0 text-secondary-600" aria-hidden />
                <span className="tabular-nums">{current.label}</span>
                <ChevronDown
                    className={`h-4 w-4 shrink-0 text-secondary-500 transition-transform motion-reduce:transition-none ${open ? 'rotate-180' : ''}`}
                    aria-hidden
                />
            </button>
            {open && (
                <ul
                    role="listbox"
                    className="absolute right-0 top-[calc(100%+0.25rem)] z-[60] min-w-[10.5rem] rounded-xl border border-secondary-200 bg-white py-1 shadow-lg ring-1 ring-secondary-900/[0.04]"
                >
                    {LANDING_LANG_OPTIONS.map((opt) => (
                        <li key={opt.id} role="option" aria-selected={lang === opt.id}>
                            <button
                                type="button"
                                className={`flex w-full items-center px-3 py-2.5 text-left text-sm font-medium transition-colors hover:bg-secondary-50 ${
                                    lang === opt.id ? 'text-brand-600' : 'text-secondary-800'
                                }`}
                                onClick={() => {
                                    onChange(opt.id);
                                    close();
                                }}
                            >
                                {opt.nativeLabel}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const RevealChild: React.FC<RevealChildProps> = ({
    revealed,
    delayMs = 0,
    className = '',
    children,
    style: styleProp,
    ...rest
}) => (
    <div
        className={`${revealChildBase} ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'} motion-reduce:opacity-100 motion-reduce:translate-y-0 ${className}`}
        style={{ transitionDelay: revealed ? `${delayMs}ms` : '0ms', ...styleProp }}
        {...rest}
    >
        {children}
    </div>
);

const LandingPage: React.FC = () => {
    const { isAuthenticated } = useAuth();
    const { i18n } = useTranslation();
    const lang: LandingLang = normalizeAppLocale(i18n.language);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const t = useMemo(() => getLandingCopy(lang), [lang]);

    const faqs = useMemo(
        (): { q: string; a: React.ReactNode }[] => [
            {
                q: t.faq1q,
                a: t.faq1a,
            },
            {
                q: t.faq2q,
                a: (
                    <>
                        {t.faq2aBeforeTerms}{' '}
                        <Link to="/terms" className={faqLinkClass}>
                            {t.termsOfService}
                        </Link>{' '}
                        {t.faq2aAfterTerms}
                    </>
                ),
            },
            {
                q: t.faq3q,
                a: (
                    <>
                        {t.faq3Prefix}
                        <a href="#pricing" className={faqLinkClass}>
                            {t.faqPricing}
                        </a>
                        {t.faq3ConnectorAB}
                        <a href="#security" className={faqLinkClass}>
                            {t.faqSecurity}
                        </a>
                        {t.faq3Middle}
                        <Link to="/privacy" className={faqLinkClass}>
                            {t.privacyPolicy}
                        </Link>
                        {t.faq3ConnectorCD}
                        <Link to="/terms" className={faqLinkClass}>
                            {t.faqTerms}
                        </Link>
                        {t.faq3Suffix}
                    </>
                ),
            },
        ],
        [t],
    );

    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const skipMotion = Boolean(prefersReducedMotion);

    const solutionReveal = useRevealOnScroll(skipMotion);
    const pricingReveal = useRevealOnScroll(skipMotion);
    const securityReveal = useRevealOnScroll(skipMotion);
    const faqReveal = useRevealOnScroll(skipMotion);
    const ctaReveal = useRevealOnScroll(skipMotion);
    const footerReveal = useRevealOnScroll(skipMotion);

    useEffect(() => {
        if (!mobileMenuOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [mobileMenuOpen]);

    const closeMobileMenu = () => setMobileMenuOpen(false);

    return (
        <div className="min-h-screen scroll-smooth bg-secondary-50 text-secondary-900 antialiased">
            <header className="sticky top-0 z-50 border-b border-secondary-200 bg-secondary-50 pt-[env(safe-area-inset-top,0px)] shadow-sm motion-safe:animate-landing-header-in motion-reduce:animate-none">
                <div className="relative mx-auto flex h-16 max-w-6xl items-center pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] sm:pl-[max(1.5rem,env(safe-area-inset-left))] sm:pr-[max(1.5rem,env(safe-area-inset-right))] md:h-[72px] lg:pl-[max(2rem,env(safe-area-inset-left))] lg:pr-[max(2rem,env(safe-area-inset-right))]">
                    <Link to="/" className="relative z-10 flex shrink-0 items-center gap-2.5 sm:gap-3" aria-label={t.homeAria}>
                        <span className="grid h-9 w-9 overflow-hidden rounded-full bg-brand-500 shadow-sm ring-1 ring-brand-500/20 sm:h-10 sm:w-10">
                            <ShieldIcon className="size-full" />
                        </span>
                        <span className="text-base font-semibold tracking-tight text-secondary-900 sm:text-lg sm:font-bold">
                            QuickMask
                        </span>
                    </Link>

                    <nav
                        className="pointer-events-none absolute left-1/2 top-1/2 z-[1] hidden -translate-x-1/2 -translate-y-1/2 md:block"
                        aria-label={t.navAria}
                    >
                        <div className="pointer-events-auto flex items-center gap-8 sm:gap-10 lg:gap-12">
                            <a
                                href="#solution"
                                className="whitespace-nowrap text-sm font-medium text-secondary-600 transition-colors hover:text-secondary-900"
                            >
                                {t.navProduct}
                            </a>
                            <a
                                href="#pricing"
                                className="whitespace-nowrap text-sm font-medium text-secondary-600 transition-colors hover:text-secondary-900"
                            >
                                {t.navPricing}
                            </a>
                            <a
                                href="#security"
                                className="whitespace-nowrap text-sm font-medium text-secondary-600 transition-colors hover:text-secondary-900"
                            >
                                {t.navSecurity}
                            </a>
                            <Link
                                to="/contact"
                                className="whitespace-nowrap text-sm font-medium text-secondary-600 transition-colors hover:text-secondary-900"
                            >
                                {t.footerContact}
                            </Link>
                        </div>
                    </nav>

                    <div className="relative z-10 ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
                        <div className="hidden items-center gap-3 md:flex">
                            {isAuthenticated ? (
                                <Link to="/app" className={`${primaryBtn} px-5 py-2.5 text-sm`}>
                                    {t.openApp}
                                </Link>
                            ) : (
                                <>
                                    <Link
                                        to="/login"
                                        className="inline-flex items-center justify-center rounded-lg border border-secondary-300 bg-white px-5 py-2.5 text-sm font-medium text-secondary-700 shadow-sm transition-colors hover:border-secondary-400 hover:bg-secondary-50"
                                    >
                                        {t.signIn}
                                    </Link>
                                    <Link to="/register" className={`${primaryBtn} px-5 py-2.5 text-sm`}>
                                        {t.signUp}
                                    </Link>
                                </>
                            )}
                        </div>
                        <LandingLangSwitcher
                            lang={lang}
                            onChange={(next) => {
                                void i18n.changeLanguage(next);
                            }}
                            ariaLabel={t.langMenuAria}
                        />
                        <button
                            type="button"
                            onClick={() => setMobileMenuOpen((v) => !v)}
                            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-secondary-200 bg-white text-secondary-700 shadow-sm transition-colors hover:bg-secondary-50 md:hidden"
                            aria-label={mobileMenuOpen ? t.closeMenu : t.openMenu}
                            aria-expanded={mobileMenuOpen}
                            aria-controls="mobile-menu"
                        >
                            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                </div>

                {mobileMenuOpen && (
                    <div id="mobile-menu" className="md:hidden">
                        <div className="border-t border-secondary-200 bg-secondary-50">
                            <nav className="mx-auto flex max-w-6xl flex-col gap-1 px-[max(1rem,env(safe-area-inset-left))] py-4 pr-[max(1rem,env(safe-area-inset-right))] sm:px-6" aria-label={t.navAria}>
                                <a
                                    href="#solution"
                                    onClick={closeMobileMenu}
                                    className="rounded-lg px-3 py-3 text-base font-medium text-secondary-700 transition-colors hover:bg-white hover:text-secondary-900"
                                >
                                    {t.navProduct}
                                </a>
                                <a
                                    href="#pricing"
                                    onClick={closeMobileMenu}
                                    className="rounded-lg px-3 py-3 text-base font-medium text-secondary-700 transition-colors hover:bg-white hover:text-secondary-900"
                                >
                                    {t.navPricing}
                                </a>
                                <a
                                    href="#security"
                                    onClick={closeMobileMenu}
                                    className="rounded-lg px-3 py-3 text-base font-medium text-secondary-700 transition-colors hover:bg-white hover:text-secondary-900"
                                >
                                    {t.navSecurity}
                                </a>
                                <Link
                                    to="/contact"
                                    onClick={closeMobileMenu}
                                    className="rounded-lg px-3 py-3 text-base font-medium text-secondary-700 transition-colors hover:bg-white hover:text-secondary-900"
                                >
                                    {t.footerContact}
                                </Link>
                            </nav>
                            <div className="mx-auto flex max-w-6xl flex-col gap-2 border-t border-secondary-200 px-[max(1rem,env(safe-area-inset-left))] py-4 pb-[max(1rem,env(safe-area-inset-bottom))] pr-[max(1rem,env(safe-area-inset-right))] sm:px-6 sm:pb-4">
                                {isAuthenticated ? (
                                    <Link to="/app" onClick={closeMobileMenu} className={`${primaryBtn} w-full`}>
                                        {t.openApp}
                                    </Link>
                                ) : (
                                    <>
                                        <Link
                                            to="/login"
                                            onClick={closeMobileMenu}
                                            className="inline-flex w-full items-center justify-center rounded-lg border border-secondary-300 bg-white px-5 py-3 text-base font-medium text-secondary-700 shadow-sm transition-colors hover:border-secondary-400 hover:bg-secondary-50"
                                        >
                                            {t.signIn}
                                        </Link>
                                        <Link to="/register" onClick={closeMobileMenu} className={`${primaryBtn} w-full`}>
                                            {t.signUp}
                                        </Link>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </header>

            <main>
                <section className="relative mx-auto max-w-6xl overflow-hidden pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] pb-12 pt-10 sm:pl-[max(1.5rem,env(safe-area-inset-left))] sm:pr-[max(1.5rem,env(safe-area-inset-right))] sm:pb-16 sm:pt-16 lg:pl-[max(2rem,env(safe-area-inset-left))] lg:pr-[max(2rem,env(safe-area-inset-right))] lg:pb-24 lg:pt-24">
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-x-0 top-[-20%] h-[min(85vh,56rem)] bg-[radial-gradient(ellipse_75%_50%_at_50%_0%,rgba(139,26,26,0.055),transparent_58%)]"
                    />
                    <div className="relative mx-auto flex max-w-[min(920px,100%)] flex-col items-center text-center">
                        <h1 className="text-landing-hero text-balance font-bold text-secondary-900 motion-safe:animate-landing-hero-in motion-safe:[animation-delay:0ms] motion-reduce:animate-none">
                            {t.heroTitleBefore}{' '}
                            <span className="text-brand-600">
                                {t.heroTitleHighlight}
                                <span className="text-secondary-900">{t.heroTitleAfterHighlight}</span>
                            </span>
                        </h1>
                        <p className="mt-5 max-w-[65ch] text-pretty text-base font-medium leading-[1.6] text-secondary-600 motion-safe:animate-landing-hero-in motion-safe:[animation-delay:45ms] motion-reduce:animate-none sm:mt-7 sm:text-lg sm:leading-[1.55]">
                            {t.heroSub1}
                        </p>
                        <p className="mt-3 max-w-[65ch] text-pretty text-base font-normal leading-[1.6] text-secondary-500 motion-safe:animate-landing-hero-in motion-safe:[animation-delay:95ms] motion-reduce:animate-none">
                            {t.heroSub2}
                        </p>
                        <div className="mt-6 flex w-full justify-center motion-safe:animate-landing-hero-in motion-safe:[animation-delay:140ms] motion-reduce:animate-none sm:mt-10 sm:w-auto">
                            <Link
                                to={isAuthenticated ? '/app' : '/try'}
                                className={`${primaryBtn} w-full max-w-xs px-6 py-3.5 sm:w-auto sm:px-8`}
                            >
                                {isAuthenticated ? t.openApp : t.tryQuickMask}
                            </Link>
                        </div>
                    </div>

                    <div
                        id="demo"
                        className="relative mx-auto mt-12 max-w-4xl scroll-mt-24 motion-safe:animate-landing-scale-in motion-safe:[animation-delay:200ms] motion-reduce:animate-none sm:mt-16 sm:scroll-mt-28 lg:mt-20"
                    >
                        <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-[#121110] shadow-[0_20px_48px_-12px_rgba(15,12,12,0.28)] ring-1 ring-inset ring-white/[0.06] sm:rounded-[1.125rem]">
                            <video
                                className="h-full w-full object-cover"
                                src="/quickmasklanding.mp4"
                                autoPlay={!prefersReducedMotion}
                                loop={!prefersReducedMotion}
                                muted
                                playsInline
                                preload={prefersReducedMotion ? 'metadata' : 'auto'}
                                controls={prefersReducedMotion}
                                aria-label={t.demoVideoAria}
                            />
                        </div>
                        <p className="mt-3 text-center text-xs font-medium uppercase tracking-[0.08em] text-secondary-500 motion-safe:animate-landing-hero-in motion-safe:[animation-delay:260ms] motion-reduce:animate-none sm:mt-4 sm:text-[0.8125rem] sm:tracking-[0.06em]">
                            {t.demoLine1}
                        </p>
                        <p className="mx-auto mt-6 max-w-[65ch] text-pretty text-center text-base font-normal leading-[1.65] text-secondary-600 motion-safe:animate-landing-hero-in motion-safe:[animation-delay:300ms] motion-reduce:animate-none">
                            {t.demoLine2}
                        </p>
                    </div>
                </section>

                {/* Product: detection + steps (replaces features, solution, how-it-works) */}
                <section
                    id="solution"
                    ref={solutionReveal.ref}
                    aria-labelledby="landing-product-heading"
                    className="scroll-mt-20 border-t border-secondary-200 bg-white py-14 sm:scroll-mt-24 sm:py-20 lg:py-28"
                >
                    <div className={`mx-auto max-w-6xl ${landingSectionInset}`}>
                        <RevealChild revealed={solutionReveal.revealed} delayMs={0} className="mx-auto max-w-[65ch] text-center">
                            <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.2em] text-brand-600 sm:text-xs sm:tracking-[0.22em]">
                                {t.productEyebrow}
                            </p>
                            <h2
                                id="landing-product-heading"
                                className="mt-4 text-balance text-landing-section font-bold text-secondary-900 sm:mt-5"
                            >
                                {t.productHeading}
                            </h2>
                            <p className="mt-5 text-pretty text-base font-normal leading-[1.65] text-secondary-600 sm:mt-6 sm:text-lg sm:leading-[1.55]">
                                {t.productSub}
                            </p>
                        </RevealChild>

                        <RevealChild revealed={solutionReveal.revealed} delayMs={40} className="mx-auto mt-8 sm:mt-10">
                            <p className="sr-only">{t.detectionSr}</p>
                            <div className="mx-auto flex max-w-2xl flex-wrap justify-center gap-2 sm:gap-2.5">
                                {t.detectionLabels.map((label) => (
                                    <span
                                        key={label}
                                        className="rounded-full border border-brand-300/40 bg-brand-50 px-3.5 py-1.5 text-sm font-medium text-secondary-800 shadow-[0_1px_0_rgba(23,23,21,0.04)]"
                                    >
                                        {label}
                                    </span>
                                ))}
                            </div>
                        </RevealChild>

                        <div className="mt-12 flex flex-col gap-6 sm:mt-14 lg:mt-[4.25rem] lg:flex-row lg:items-stretch lg:justify-center lg:gap-5">
                            {t.steps.map((step, index) => {
                                const Icon = productStepIcons[index];
                                const accent = index === 2 ? 'success' : 'brand';
                                return (
                                <Fragment key={step.title}>
                                    <RevealChild
                                        revealed={solutionReveal.revealed}
                                        delayMs={80 + index * 36}
                                        className={`flex flex-1 flex-col rounded-2xl border border-secondary-200/90 bg-secondary-50/90 p-5 shadow-sm shadow-secondary-900/[0.03] transition-[border-color,box-shadow,background-color] duration-200 ease-out hover:border-secondary-300 hover:bg-secondary-50 hover:shadow-md hover:shadow-secondary-900/[0.04] sm:p-6 lg:max-w-none motion-safe:lg:transition-transform motion-safe:lg:duration-300 motion-safe:lg:ease-[cubic-bezier(0.25,1,0.5,1)] ${
                                            index === 1
                                                ? 'motion-safe:lg:z-[1] motion-safe:lg:-translate-y-1 motion-safe:lg:shadow-md motion-safe:lg:shadow-secondary-900/[0.06]'
                                                : ''
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span
                                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-bold tabular-nums text-white ${
                                                    accent === 'success' ? 'bg-success-600' : 'bg-brand-500'
                                                }`}
                                            >
                                                {index + 1}
                                            </span>
                                            <span
                                                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                                                    accent === 'success'
                                                        ? 'text-success-600'
                                                        : 'bg-brand-500/12 text-brand-500'
                                                }`}
                                                aria-hidden
                                            >
                                                <Icon className="h-5 w-5" strokeWidth={1.75} />
                                            </span>
                                        </div>
                                        <h3 className="mt-5 text-lg font-semibold tracking-tight text-secondary-900 sm:text-xl">
                                            {step.title}
                                        </h3>
                                        <p className="mt-2.5 text-base font-normal leading-[1.65] text-secondary-600">{step.body}</p>
                                    </RevealChild>
                                    {index < t.steps.length - 1 && (
                                        <div
                                            className="hidden shrink-0 items-center justify-center self-center lg:flex lg:pb-10"
                                            aria-hidden
                                        >
                                            <ArrowRight className="h-7 w-7 text-brand-500/55" strokeWidth={1.5} />
                                        </div>
                                    )}
                                </Fragment>
                                );
                            })}
                        </div>

                        <RevealChild revealed={solutionReveal.revealed} delayMs={200} className="mt-10 text-center sm:mt-12">
                            <a
                                href="#demo"
                                className={`${faqLinkClass} ${landingAccentLinkFocus} inline-flex items-center gap-1 px-1 py-0.5 text-base font-semibold`}
                            >
                                {t.seeDemo}
                                <span aria-hidden>→</span>
                            </a>
                        </RevealChild>
                    </div>
                </section>

                <section
                    id="pricing"
                    ref={pricingReveal.ref}
                    className="scroll-mt-20 border-t border-secondary-200 bg-secondary-50 py-14 sm:scroll-mt-24 sm:py-20 lg:py-24"
                >
                    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                        <RevealChild revealed={pricingReveal.revealed} delayMs={0} className="text-center">
                            <h2 className="text-landing-section font-bold text-secondary-900">{t.pricingHeading}</h2>
                        </RevealChild>
                        <RevealChild
                            revealed={pricingReveal.revealed}
                            delayMs={36}
                            className="mt-6 max-w-[65ch] text-pretty text-base font-normal leading-[1.65] text-secondary-600 sm:mx-auto sm:mt-8 sm:text-lg sm:leading-[1.55]"
                        >
                            <p>{t.pricingIntro}</p>
                            <ul className="mt-6 list-disc space-y-3 pl-5 marker:text-secondary-400">
                                <li>
                                    <strong className="font-semibold text-secondary-900">{t.pricingFreeBold}</strong> {t.pricingFreeRest}
                                </li>
                                <li>
                                    <strong className="font-semibold text-secondary-900">{t.pricingPaidBold}</strong> {t.pricingPaidRest}
                                </li>
                            </ul>
                            <p className="mt-6 text-base text-secondary-500">
                                {t.legalTermsLead}{' '}
                                <Link to="/privacy" className={faqLinkClass}>
                                    {t.privacyPolicy}
                                </Link>
                                {t.pricingBetweenLegalLinks}
                                <Link to="/terms" className={faqLinkClass}>
                                    {t.termsOfService}
                                </Link>
                                {t.pricingAfterLegalBlock}
                                {t.moreQuestionsFaq}{' '}
                                <a href="#faq" className={faqLinkClass}>
                                    {t.footerFaq}
                                </a>
                                {t.pricingLegalClosing}
                            </p>
                            {!isAuthenticated && (
                                <div className="mt-8 flex justify-center sm:mt-10">
                                    <Link to="/register" className={`${primaryBtn} px-8`}>
                                        {t.createFreeAccount}
                                    </Link>
                                </div>
                            )}
                        </RevealChild>
                    </div>
                </section>

                <section
                    id="security"
                    ref={securityReveal.ref}
                    className="scroll-mt-20 border-t border-secondary-200 bg-white py-14 sm:scroll-mt-24 sm:py-20 lg:py-24"
                >
                    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                        <RevealChild revealed={securityReveal.revealed} delayMs={0} className="text-center">
                            <h2 className="text-landing-section font-bold text-secondary-900">{t.securityHeading}</h2>
                        </RevealChild>
                        <RevealChild
                            revealed={securityReveal.revealed}
                            delayMs={40}
                            className="mt-6 max-w-[65ch] space-y-4 text-pretty text-base font-normal leading-[1.65] text-secondary-600 sm:mx-auto sm:mt-8 sm:text-lg sm:leading-[1.55]"
                        >
                            {(
                                [
                                    { title: t.security1Title, body: t.security1Body },
                                    { title: t.security2Title, body: t.security2Body },
                                    { title: t.security3Title, body: t.security3Body },
                                ] as const
                            ).map(({ title, body }) => (
                                <p key={title}>
                                    <strong className="font-semibold text-secondary-900">{title}</strong>
                                    {' \u2014 '}
                                    {body}
                                </p>
                            ))}
                            <p>
                                {t.securityP3Before}{' '}
                                <Link to="/privacy" className={faqLinkClass}>
                                    {t.privacyPolicy}
                                </Link>
                                {t.securityP3After}{' '}
                                <Link to="/terms" className={faqLinkClass}>
                                    {t.termsOfService}
                                </Link>
                                .
                            </p>
                            <p className="text-base text-secondary-500">
                                {t.securityP4}{' '}
                                <a href="#faq" className={faqLinkClass}>
                                    {t.footerFaq}
                                </a>
                                {t.pricingLegalClosing}
                            </p>
                        </RevealChild>
                    </div>
                </section>

                <section
                    id="faq"
                    ref={faqReveal.ref}
                    className="scroll-mt-20 border-t border-secondary-200 bg-secondary-50 py-14 sm:scroll-mt-24 sm:py-20 lg:py-24"
                >
                    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                        <RevealChild revealed={faqReveal.revealed} delayMs={0} className="text-center">
                            <h2 className="text-landing-section font-bold text-secondary-900">{t.faqHeading}</h2>
                        </RevealChild>
                        <RevealChild revealed={faqReveal.revealed} delayMs={36} className="mt-8 space-y-3 sm:mt-10">
                            {faqs.map((item) => (
                                <details
                                    key={item.q}
                                    className="group rounded-xl border border-secondary-200/90 bg-white px-4 py-4 shadow-sm ring-1 ring-transparent transition-[box-shadow,ring-color,border-color] open:border-brand-200/60 open:shadow-md open:shadow-secondary-900/[0.04] open:ring-brand-900/[0.02] sm:px-6"
                                >
                                    <summary className="cursor-pointer list-none text-left text-base font-semibold leading-snug text-secondary-900 marker:content-none [&::-webkit-details-marker]:hidden sm:text-[1.0625rem] sm:leading-snug">
                                        <span className="flex items-center justify-between gap-3 sm:gap-4">
                                            {item.q}
                                            <ChevronDown className="h-5 w-5 shrink-0 text-secondary-400 transition-transform motion-reduce:transition-none group-open:rotate-180" />
                                        </span>
                                    </summary>
                                    <div className="mt-3 text-base font-normal leading-[1.65] text-secondary-600">{item.a}</div>
                                </details>
                            ))}
                        </RevealChild>
                    </div>
                </section>

                <section
                    ref={ctaReveal.ref}
                    className="relative overflow-hidden bg-gradient-to-br from-[#1a1514] via-[#0f0d0d] to-[#121010] pt-10 pb-[max(2.5rem,env(safe-area-inset-bottom))] text-[#f5f2f2] sm:pt-12 sm:pb-[max(3rem,env(safe-area-inset-bottom))]"
                >
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-x-[-20%] bottom-[-40%] h-[120%] bg-[radial-gradient(ellipse_55%_40%_at_70%_100%,rgba(139,36,34,0.14),transparent_62%)]"
                    />
                    <div className="relative mx-auto flex max-w-6xl flex-col items-center justify-between gap-7 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] text-center sm:flex-row sm:gap-10 sm:pl-[max(1.5rem,env(safe-area-inset-left))] sm:pr-[max(1.5rem,env(safe-area-inset-right))] sm:text-left lg:pl-[max(2rem,env(safe-area-inset-left))] lg:pr-[max(2rem,env(safe-area-inset-right))]">
                        <RevealChild revealed={ctaReveal.revealed} delayMs={0} className="max-w-[65ch]">
                            <p className="text-xl font-bold tracking-[-0.02em] text-[#faf7f7] sm:text-2xl sm:leading-[1.25]">
                                {t.ctaHeading}
                            </p>
                            <p className="mt-3 text-base font-normal leading-[1.65] text-secondary-400">
                                {isAuthenticated ? t.ctaSubIn : t.ctaSubOut}
                            </p>
                        </RevealChild>
                        <RevealChild revealed={ctaReveal.revealed} delayMs={44} className="w-full shrink-0 sm:w-auto">
                            <Link to={isAuthenticated ? '/app' : '/register'} className={`${primaryBtn} !px-8 w-full sm:w-auto`}>
                                {isAuthenticated ? t.openApp : t.signUp}
                            </Link>
                        </RevealChild>
                    </div>
                </section>

                <footer ref={footerReveal.ref} className="border-t border-secondary-800 bg-[#0a0a0a] py-10 pb-[max(2.5rem,env(safe-area-inset-bottom))] text-secondary-400 sm:py-12 sm:pb-[max(3rem,env(safe-area-inset-bottom))]">
                    <RevealChild
                        revealed={footerReveal.revealed}
                        delayMs={0}
                        className="mx-auto max-w-6xl pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] sm:pl-[max(1.5rem,env(safe-area-inset-left))] sm:pr-[max(1.5rem,env(safe-area-inset-right))] lg:pl-[max(2rem,env(safe-area-inset-left))] lg:pr-[max(2rem,env(safe-area-inset-right))]"
                    >
                        <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between lg:gap-10">
                            <div className="flex items-center gap-3">
                            <span className="grid h-9 w-9 overflow-hidden rounded-full bg-brand-500 shadow-sm ring-1 ring-brand-500/20">
                                <ShieldIcon className="size-full" />
                            </span>
                            <div>
                                <p className="font-semibold tracking-tight text-white">QuickMask</p>
                                <p className="text-sm font-normal leading-normal text-secondary-400">{t.footerTagline}</p>
                            </div>
                            </div>
                            <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm font-medium sm:gap-x-8">
                            <a href="#solution" className="hover:text-white">
                                {t.navProduct}
                            </a>
                            <a href="#pricing" className="hover:text-white">
                                {t.navPricing}
                            </a>
                            <a href="#security" className="hover:text-white">
                                {t.navSecurity}
                            </a>
                            <a href="#faq" className="hover:text-white">
                                {t.footerFaq}
                            </a>
                            <Link to="/privacy" className="hover:text-white">
                                {t.footerPrivacy}
                            </Link>
                            <Link to="/terms" className="hover:text-white">
                                {t.footerTerms}
                            </Link>
                            <Link to="/contact" className="hover:text-white">
                                {t.footerContact}
                            </Link>
                            </div>
                            <div className="flex items-center gap-4">
                            <a
                                href="https://github.com/matthewyijielu0317/QuickMask"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-secondary-400 hover:text-white"
                                aria-label="GitHub"
                            >
                                <Github className="h-5 w-5" />
                            </a>
                            <a href="mailto:matthewma003@gmail.com" className="text-secondary-400 hover:text-white" aria-label="Email">
                                <Mail className="h-5 w-5" />
                            </a>
                            </div>
                        </div>
                        <p className="mt-8 text-center text-xs text-secondary-600 sm:mt-10">
                            © {new Date().getFullYear()} QuickMask. {t.copyright}
                        </p>
                    </RevealChild>
                </footer>
            </main>
        </div>
    );
};

export default LandingPage;
